import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MuiColorInput } from 'mui-color-input';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import FileUpload from '../../../../shared/components/FileUpload';
import { ImageHandler } from '../../../../shared/components/ImageHandler';
import { ETheme } from '../../../../shared/enums/user/ETheme';
import { checkFavicon } from '../../../../shared/functions/checkFavicon';
import { handleError } from '../../../../shared/functions/handleError';
import { ICompanySettings } from '../../../../shared/interfaces/settings/ISettings';
import { darkPalette, lightPalette } from '../../../../shared/mui/palettes';
import authSlice from '../../../../store/slices/auth.slice';
import companySettingsSlice from '../../../../store/slices/company-settings.slice';
import { RootState } from '../../../../store/store';
import { getFileType } from '../../employees/components/tabs/Documents';
import { SettingsService } from '../service';

export const companySettingsSchema = yup.object().shape({
  title: yup
    .string()
    .required('validation.required')
    .min(2, 'validation.minLength'),
  primaryColor: yup
    .string()
    .matches(
      /^#[0-9A-Fa-f]{6}$/,
      'Primary color must be a valid hex code (e.g., #FF5733)',
    )
    .required('validation.required'),
});
const CompanyInformation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state?.auth);
  const company = useSelector((state: RootState) => state?.companySettings);
  const [isLoading, setIsLoading] = useState(false);
  const { handleSubmit, control, watch, reset } = useForm<ICompanySettings>({
    resolver: yupResolver(
      companySettingsSchema,
    ) as unknown as Resolver<ICompanySettings>,
    defaultValues: async () => {
      return company as ICompanySettings;
    },
  });

  const submitHandler = async (companySettings: ICompanySettings) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append(
      'body',
      JSON.stringify({
        title: companySettings?.title,
        primaryColor: companySettings?.primaryColor,
      }),
    );
    if (companySettings?.file) {
      formData.append('file', companySettings?.file);
    }

    if (companySettings?.invertedFile) {
      formData.append('invertedFile', companySettings?.invertedFile);
    }
    if (companySettings?.faviconFile) {
      formData.append('faviconFile', companySettings?.faviconFile);
    }
    await SettingsService.update(formData)
      .then((response) => {
        const jwtToken = localStorage.getItem('token');
        enqueueSnackbar(
          t('toasts.successfullyUpdated', {
            title: companySettings?.title,
          }),
          { variant: 'success' },
        );
        dispatch(
          authSlice.actions.setLoginState({
            userData,
            token: jwtToken,
            isAuthenticated: true,
          }),
        );
        localStorage.setItem('companyName', response?.title);
        localStorage.setItem('primaryColor', response?.primaryColor);
        localStorage.setItem('faviconPath', response?.faviconPath);
        document.title = `${response?.title} Management`;
        checkFavicon(response?.faviconPath);
        dispatch(companySettingsSlice.actions.setCompanySettings(response));
        reset(response);
      })
      .catch(handleError)
      .finally(() => {
        setIsLoading(false);
      });
  };
  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Card>
        <Typography variant={'h6'} fontWeight={'600'}>
          {t(`settings.companyInformations`)}
        </Typography>
        <Typography
          variant={'subtitle2'}
          color={lightPalette.text.gray}
          sx={{
            fontWeight: '300',
          }}
        >
          {t(`settings.companyInformations_desc`)}
        </Typography>
        <Box sx={{ mt: '20px' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={'title'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message as string, { minLength: 2 })}
                    value={value ?? ''}
                    onChange={onChange}
                    label={t('general.name')}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={'primaryColor'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <MuiColorInput
                    format="hex"
                    value={value ?? ''}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message as string)}
                    label={t('general.primaryColor')}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant={'subtitle2'}
                color={lightPalette.text.gray}
                sx={{
                  fontWeight: '300',
                }}
              >
                {t(`settings.logo`)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Controller
                  name={'file'}
                  key={watch('updatedAt')}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <>
                        <FileUpload
                          file={value ?? null}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            onChange(file);
                          }}
                        />
                        {!!error?.message?.length && (
                          <FormHelperText>{t(error?.message)}</FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
                {watch('thumbnail') && (
                  <ImageHandler
                    src={watch('thumbnail')}
                    alt={`File preview - thumbnail`}
                    className={'profile-backup-image ms-3'}
                    style={{
                      borderColor:
                        userData?.theme === ETheme.LIGHT
                          ? lightPalette.primary.light
                          : darkPalette.background.paper,
                    }}
                    tooltipTitle={watch('originalName')}
                    fallbackType={getFileType(watch('mime'))}
                  />
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant={'subtitle2'}
                color={lightPalette.text.gray}
                sx={{
                  fontWeight: '300',
                }}
              >
                {t(`settings.logoInverted`)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Controller
                  name={'invertedFile'}
                  key={watch('updatedAt')}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <>
                        <FileUpload
                          file={value ?? null}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            onChange(file);
                          }}
                        />
                        {!!error?.message?.length && (
                          <FormHelperText>{t(error?.message)}</FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
                {watch('invertedThumbnail') && (
                  <ImageHandler
                    src={watch('invertedThumbnail')}
                    alt={`File preview - inverted Thumbnail`}
                    className={'profile-backup-image ms-3'}
                    style={{
                      borderColor:
                        userData?.theme === ETheme.LIGHT
                          ? lightPalette.primary.light
                          : darkPalette.background.paper,
                    }}
                    tooltipTitle={watch('invertedThumbnail')}
                    fallbackType={getFileType(watch('mime'))}
                  />
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant={'subtitle2'}
                color={lightPalette.text.gray}
                sx={{
                  fontWeight: '300',
                }}
              >
                {t(`settings.favicon`)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Controller
                  name={'faviconFile'}
                  key={watch('updatedAt')}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <>
                        <FileUpload
                          file={value ?? null}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            onChange(file);
                          }}
                        />
                        {!!error?.message?.length && (
                          <FormHelperText>{t(error?.message)}</FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
                {watch('faviconPath') && (
                  <ImageHandler
                    src={watch('faviconPath')}
                    alt={`File preview - favicon path`}
                    className={'profile-backup-image ms-3'}
                    style={{
                      borderColor:
                        userData?.theme === ETheme.LIGHT
                          ? lightPalette.primary.light
                          : darkPalette.background.paper,
                    }}
                    tooltipTitle={watch('faviconPath')}
                    fallbackType={getFileType(watch('mime'))}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            loading={isLoading}
            variant={'contained'}
            type={'submit'}
            sx={{ margin: '20px 0 0 10px' }}
          >
            {t('button.update')}
          </LoadingButton>
        </Box>
      </Card>
    </form>
  );
};

export default CompanyInformation;
